import React, { useEffect, useMemo, useState } from 'react';
import Text from './common/Text';
import Button from './common/Button';
import CustomSelect from './common/Select';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { MultiValue, SingleValue } from 'react-select';
import { getFilterUsers, getUsersCount } from '../api/UserService';
import {
  FilterUsersResponse,
  UserFilterParams,
  UserCountResponse,
} from '../types/types';
import Loading from './common/Loading';

interface PeopleFilterProps {
  toggleFilter: (isVisible: boolean) => void;
  handleFilterUsers: (peopleFilter: UserFilterParams) => void;
}

interface User {
  id: number;
  name: string;
}

const PeopleFilter: React.FC<PeopleFilterProps> = ({
  toggleFilter,
  handleFilterUsers,
}) => {
  const reduxUserTypes = useSelector(
    (state: RootState) => state.userTypes.userTypes,
  );
  const reduxSchools = useSelector((state: RootState) => state.schools.schools);
  const reduxDepartments = useSelector(
    (state: RootState) => state.departments.departments,
  );
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const newUserType = { id: 0, type: 'All' };

  const [filterUsers, setFilterUsers] = useState<User[] | null>([]);

  const [selectedUsers, setSelectedUsers] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedRole, setSelectedRole] = useState<number>(0);
  const [selectedSortBy, setSelectedSortBy] = useState<'asc' | 'desc'>('asc');
  const [selectedSchools, setSelectedSchools] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedDepartments, setSelectedDepartments] = useState<
    { id: number; name: string }[]
  >([]);
  const [selectedDepartmentsBySchool, setSelectedDepartmentsBySchool] =
    useState<{ id: number; name: string }[]>([]);

  const parsedUserTypesOptions = useMemo(() => {
    return [newUserType, ...reduxUserTypes];
  }, [reduxUserTypes]);
  const [loading, setLoading] = useState<boolean | null>(null);
  const [departmentsOptionsMessage, setDepartmentsOptionsMessage] = useState<
    string | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const fetchUsersCount = async () => {
      const countResponse = await getUsersCount(
        selectedUsers,
        selectedRole,
        selectedSchools.length === 1 ? selectedSchools[0].id : null,
        selectedDepartments.length === 1 ? selectedDepartments[0].id : null,
      );
      if ('code' in countResponse) {
        console.error('Error fetching users count:', countResponse);
        if (countResponse.code === 'NOT_FOUND') {
          setTotalCount(0);
        } else {
          setTotalCount(0);
        }
        setLoading(false);
      } else {
        setTotalCount((countResponse as UserCountResponse).total_count);
        setLoading(false);
      }
    };
    fetchUsersCount();
  }, [selectedSchools, selectedDepartments, selectedUsers, selectedRole]);

  useEffect(() => {
    const fetchFilterUsers = async () => {
      const filterUsersResponse = await getFilterUsers(
        selectedRole,
        selectedSchools.length === 1 ? selectedSchools[0].id : null,
        selectedDepartments.length === 1 ? selectedDepartments[0].id : null,
      );
      if ('code' in filterUsersResponse) {
        console.error('Error fetching filter users:', filterUsersResponse);
        if (filterUsersResponse.code !== 'NOT_FOUND')
          setError(filterUsersResponse.message);
        setFilterUsers([]);
      } else {
        setError(null);
        setFilterUsers((filterUsersResponse as FilterUsersResponse).users);
      }
    };
    fetchFilterUsers();
  }, [selectedSchools, selectedDepartments, selectedUsers, selectedRole]);

  const selectUsers = (
    selectedOptions:
      | SingleValue<{ value: number; label: string }>
      | MultiValue<{ value: number; label: string }>,
  ) => {
    setError(null);
    if (Array.isArray(selectedOptions)) {
      const users = selectedOptions.map((option) => ({
        id: option.value,
        name: option.label,
      }));
      setSelectedUsers(users);
    } else if (selectedOptions) {
      const singleOption = selectedOptions as SingleValue<{
        value: number;
        label: string;
      }>;
      setSelectedUsers(
        singleOption
          ? [{ id: singleOption.value, name: singleOption.label }]
          : [],
      );
    } else {
      setSelectedUsers([]);
    }
  };

  const selectSchools = (
    selectedOptions:
      | SingleValue<{ value: number; label: string }>
      | MultiValue<{ value: number; label: string }>,
  ) => {
    setError(null);
    if (Array.isArray(selectedOptions)) {
      const schools = selectedOptions.map((option) => ({
        id: option.value,
        name: option.label,
      }));
      setSelectedSchools(schools);
      const departmentsBySchoolsArray: { id: number; name: string }[] = [];
      reduxDepartments.map((department) => {
        selectedOptions.map((option) => {
          if (department.school_id === option.value) {
            departmentsBySchoolsArray.push({
              id: department.id,
              name: department.name,
            });
          }
        });
      });
      if (departmentsBySchoolsArray.length === 0) {
        console.log('No matching departments found.');
        setSelectedDepartmentsBySchool([]);
        setSelectedDepartments([]);
        setDepartmentsOptionsMessage('No departments available');
      } else {
        setSelectedDepartmentsBySchool(departmentsBySchoolsArray);
      }
    } else if (selectedOptions) {
      const singleOption = selectedOptions as SingleValue<{
        value: number;
        label: string;
      }>;
      setSelectedSchools(
        singleOption
          ? [{ id: singleOption.value, name: singleOption.label }]
          : [],
      );
      const departmentsBySchoolsArray: { id: number; name: string }[] = [];
      if (singleOption !== null) {
        reduxDepartments.map((department) => {
          if (department.school_id === singleOption.value) {
            departmentsBySchoolsArray.push({
              id: department.id,
              name: department.name,
            });
          }
        });
        if (departmentsBySchoolsArray.length === 0) {
          console.log('No matching departments found.');
          setSelectedDepartmentsBySchool([]);
          setSelectedDepartments([]);
          setSelectedUsers([]);
          setDepartmentsOptionsMessage(
            'There are no departments available for the selected school. Please select a different school to see available departments.',
          );
        } else {
          setSelectedUsers([]);
          setSelectedDepartmentsBySchool(departmentsBySchoolsArray);
        }
      }
    } else {
      setSelectedSchools([]);
      setSelectedDepartments([]);
      setSelectedDepartmentsBySchool([]);
      setSelectedUsers([]);
      setDepartmentsOptionsMessage(
        'Please select a school first to see available departments.',
      );
    }
  };

  const handleRole = (userTypeId: number) => {
    setError(null);
    setSelectedRole(userTypeId);
  };

  const handleSortBy = (value: 'asc' | 'desc') => {
    setError(null);
    setSelectedSortBy(value);
  };

  const selectDepartments = (
    selectedOptions:
      | SingleValue<{ value: number; label: string }>
      | MultiValue<{ value: number; label: string }>,
  ) => {
    setError(null);
    if (Array.isArray(selectedOptions)) {
      const departments = selectedOptions.map((option) => ({
        id: option.value,
        name: option.label,
      }));
      setSelectedDepartments(departments);
    } else if (selectedOptions) {
      const singleOption = selectedOptions as SingleValue<{
        value: number;
        label: string;
      }>;
      setSelectedDepartments(
        singleOption
          ? [{ id: singleOption.value, name: singleOption.label }]
          : [],
      );
    } else {
      setSelectedDepartments([]);
    }
  };

  const handleFilters = () => {
    handleFilterUsers({
      user_type_id: selectedRole ? [selectedRole] : [],
      department_id:
        selectedDepartments.length === 1
          ? selectedDepartments[0].id
          : undefined,
      school_id:
        selectedSchools.length === 1 ? selectedSchools[0].id : undefined,
      sortBy: selectedSortBy,
      userId: selectedUsers.length === 1 ? selectedUsers[0].id : undefined,
    });
  };

  const handNumberOfUsersisZero = () => {
    console.log('no users found');
  };

  return (
    <div className="filter-container">
      <div className="filter-sub-container grid gap-25 col-lg-4 col-md-6 col-sm-8 col-xs-12">
        <div className="filter-header">
          <Text className="medium-bold">Filter People</Text>
          <Button
            type="button"
            className="small-semi-bold filter-close-button"
            onClick={() => toggleFilter(false)}
          >
            close
          </Button>
        </div>
        <div className="line" />
        <div className="filter-selections">
          <div className="form-row">
            <Text className="section-data">University Role</Text>
            <Text className="filter-button-group">
              {parsedUserTypesOptions.map((userType, index) => (
                <Button
                  type="button"
                  key={index}
                  className={`role-button ${
                    selectedRole === userType.id ? 'selected' : ''
                  }`}
                  onClick={() => handleRole(userType.id)}
                >
                  {userType.type}
                </Button>
              ))}
            </Text>
          </div>
          <div className="form-row">
            <Text className="section-data">School</Text>
            <CustomSelect
              options={reduxSchools.map((school) => ({
                label: school.name,
                value: school.id,
              }))}
              onChange={selectSchools}
              value={selectedSchools.map((school) => ({
                label: school.name,
                value: school.id,
              }))}
              placeholder="Select School"
              isClearable={true}
              noOptionsMessage="No schools available"
            />
          </div>
          <div className="form-row">
            <Text className="section-data">Department</Text>
            <CustomSelect
              options={selectedDepartmentsBySchool.map((department) => ({
                label: department.name,
                value: department.id,
              }))}
              onChange={selectDepartments}
              value={selectedDepartments.map((department) => ({
                label: department.name,
                value: department.id,
              }))}
              placeholder="Select Department"
              isClearable={true}
              noOptionsMessage={
                departmentsOptionsMessage !== null
                  ? departmentsOptionsMessage
                  : 'Please select a school first to see available departments.'
              }
            />
          </div>
          <div className="form-row">
            <Text className="section-data">User Name</Text>
            <CustomSelect
              options={
                filterUsers?.map((user) => ({
                  label: user.name,
                  value: user.id,
                })) || []
              }
              onChange={selectUsers}
              value={selectedUsers.map((user) => ({
                label: user.name,
                value: user.id,
              }))}
              placeholder="Select User"
              isClearable={true}
              noOptionsMessage="No users found. Please adjust your search criteria."
              menuPlacement="top"
            />
          </div>
          <div className="form-row">
            <Text className="section-data">Sort by</Text>
            <div className="filter-button-group">
              <Button
                type="button"
                className={`role-button ${selectedSortBy === 'asc' ? 'selected' : ''}`}
                onClick={() => handleSortBy('asc')}
              >
                A-Z
              </Button>
              <Button
                type="button"
                className={`role-button ${selectedSortBy === 'desc' ? 'selected' : ''}`}
                onClick={() => handleSortBy('desc')}
              >
                Z-A
              </Button>
            </div>
          </div>
        </div>
        <div className="line" />
        {error && (
          <div className="error-message">
            <div>{error}</div>
          </div>
        )}
        <Button
          type="submit"
          className={`tertiary-button ${totalCount === 0 ? 'disabled-button' : ''}`}
          onClick={totalCount !== 0 ? handleFilters : handNumberOfUsersisZero}
        >
          {loading ? (
            <>
              <Loading size={25} /> &nbsp; Show Results
            </>
          ) : (
            'Show ' + totalCount + ' Results'
          )}
        </Button>
      </div>
    </div>
  );
};

export default PeopleFilter;
